import {
  useState,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react'
import { useLocation, useHistory } from 'react-router-dom'

type QueryStateType = string | string[] | number | Date | null

export const useQueryState = <T extends QueryStateType>(
  initialState: T,
  paramName: string,
): [
  T,
  Dispatch<SetStateAction<T>>,
  (values: Record<string, QueryStateType>) => void,
] => {
  const location = useLocation()
  const history = useHistory()

  const [state, setState] = useState<T>(() => {
    const queryParams = new URLSearchParams(location.search)
    const paramValue = queryParams.get(paramName)

    if (paramValue && Array.isArray(initialState)) {
      try {
        const parsedValue = JSON.parse(paramValue)
        if (Array.isArray(parsedValue)) {
          return parsedValue as T
        }
      } catch {
        // If parsing fails, fallback to initialState
      }
    } else if (!isNaN(Number(paramValue)) && typeof initialState === 'number') {
      return Number(paramValue) as T
    } else if (
      paramValue &&
      !isNaN(Date.parse(paramValue)) &&
      (initialState instanceof Date || initialState === null)
    ) {
      return new Date(paramValue) as T
    } else {
      return (paramValue || initialState) as T
    }

    return initialState
  })

  const updateQueryParam = useCallback(
    (value: T) => {
      const queryParams = new URLSearchParams(location.search)

      if (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        if (Array.isArray(value)) {
          queryParams.set(paramName, JSON.stringify(value))
        } else if (value instanceof Date) {
          queryParams.set(paramName, value.toISOString())
        } else {
          queryParams.set(paramName, String(value))
        }
      } else {
        queryParams.delete(paramName)
      }

      history.replace({ search: queryParams.toString() })
    },
    [location.search, history, paramName],
  )

  useEffect(() => {
    updateQueryParam(state)
  }, [state])

  const updateMultipleQueryParams = useCallback(
    (values: Record<string, QueryStateType>) => {
      const queryParams = new URLSearchParams(location.search)

      Object.keys(values).forEach(key => {
        const value = values[key]
        if (
          value !== null &&
          value !== undefined &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0)
        ) {
          if (Array.isArray(value)) {
            queryParams.set(key, JSON.stringify(value))
          } else if (value instanceof Date) {
            queryParams.set(key, value.toISOString())
          } else {
            queryParams.set(key, String(value))
          }
        } else {
          queryParams.delete(key)
        }
      })

      history.replace({ search: queryParams.toString() })
    },
    [location.search],
  )

  return [state, setState, updateMultipleQueryParams]
}
